import { useCallback } from 'react';
import { isNil } from '~/utilities/type-guards';
import { paths } from '~/singlePageApp/paths';
import { UPGRADE_PURCHASE_TYPE_BY_LEVEL } from '~/singlePageApp/components/Plans/tieredPlans';
import { useTieredPlans } from '~/singlePageApp/components/Plans/useTieredPlans';
import { useGetUsePurchaseLinkDataLazyQuery } from './__gql/usePurchaseLink.gql.gen';

interface UsePurchaseLinkReturn {
  buildLink: () => Promise<{ isGetInTouch: boolean; path: string }>;
}

export const usePurchaseLink = (): UsePurchaseLinkReturn => {
  const [load] = useGetUsePurchaseLinkDataLazyQuery();
  const { getBySKU } = useTieredPlans();

  return {
    buildLink: useCallback(async () => {
      const { data } = await load();

      if (isNil(data) || isNil(data.currentAccount) || isNil(data.currentContact)) {
        return {
          path: paths.plans(),
          isGetInTouch: false,
        };
      }

      const { currentAccount, currentContact } = data;
      const { plan } = currentAccount;

      const currentPlan = getBySKU(plan?.sku ?? '');
      if (!plan?.tiered || isNil(currentPlan) || currentPlan.tier === 'advanced') {
        return {
          path: paths.plans(),
          isGetInTouch: currentPlan?.tier === 'advanced',
        };
      }

      const purchaseType = UPGRADE_PURCHASE_TYPE_BY_LEVEL[currentPlan.level];
      const isGetInTouch = isNil(purchaseType);

      return {
        isGetInTouch,
        path:
          (!currentContact.isOwner && !currentContact.isWadmin) || isGetInTouch
            ? paths.plans()
            : paths.payment({ planType: purchaseType }),
      };
    }, [load, getBySKU]),
  };
};
